import { useState } from "react";
import { MessageCircle, Mic, X, Copy, Download } from "lucide-react";
import { useVoiceChannel, type VoiceSample } from "./VoiceChannelProvider";
import { VoicePlayerInline } from "./VoicePlayerInline";
import { cn } from "@/lib/utils";

// Cross-referenced from the my-jarvis-voice deployment for now. Follow-up:
// host avatars in our own R2 bucket so we don't depend on another app
// being deployed.
const AVATAR_BASE = "https://my-jarvis-voice.vercel.app/avatars";

const AGENT_META: Record<string, { label: string; color: string; avatar: string }> = {
  jarvis: { label: "Jarvis", color: "#2563eb", avatar: `${AVATAR_BASE}/jarvis.jpg` },
  atlas: { label: "Atlas", color: "#ea580c", avatar: `${AVATAR_BASE}/atlas.jpg` },
  nova: { label: "Nova", color: "#7c3aed", avatar: `${AVATAR_BASE}/nova.jpg` },
  echo: { label: "Echo", color: "#16a34a", avatar: `${AVATAR_BASE}/echo.jpg` },
  bolt: { label: "Bolt", color: "#f59e0b", avatar: "" },
  spark: { label: "Spark", color: "#ef4444", avatar: "" },
};

function AgentAvatar({ agentName }: { agentName: string | null }) {
  const key = (agentName || "").toLowerCase();
  const meta = key ? AGENT_META[key] : null;
  if (!meta) {
    return (
      <div className="w-7 h-7 rounded-full bg-gray-100 shrink-0 flex items-center justify-center">
        <Mic className="w-3.5 h-3.5 text-gray-400" />
      </div>
    );
  }
  if (meta.avatar) {
    return (
      <img
        src={meta.avatar}
        alt={meta.label}
        className="w-7 h-7 rounded-full shrink-0 object-cover ring-1 ring-gray-200"
      />
    );
  }
  return (
    <div
      className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-white text-xs font-bold ring-1 ring-gray-200"
      style={{ backgroundColor: meta.color }}
    >
      {meta.label[0]}
    </div>
  );
}

function ExpandableText({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <p
      className={`text-sm text-[#555] leading-relaxed ${expanded ? "" : "line-clamp-3"} cursor-pointer`}
      onClick={(e) => {
        e.stopPropagation();
        setExpanded(!expanded);
      }}
    >
      {text}
    </p>
  );
}

function AutoplayToggle({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex shrink-0 cursor-pointer select-none items-center gap-1.5 text-[11px] text-muted-foreground">
      <span>Auto-play</span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label="Auto-play new voice messages"
        onClick={() => onChange(!enabled)}
        className={cn(
          "relative h-4 w-7 shrink-0 rounded-full border transition-colors",
          enabled ? "border-foreground bg-foreground" : "border-border bg-muted",
        )}
      >
        <span
          className={cn(
            "absolute top-[1px] h-[10px] w-[10px] rounded-full bg-background transition-transform",
            enabled ? "translate-x-[15px]" : "translate-x-[2px]",
          )}
        />
      </button>
    </label>
  );
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString("he-IL", { hour: "numeric", minute: "2-digit", hour12: false });
}

function groupByDay(samples: VoiceSample[]) {
  const groups: Record<string, VoiceSample[]> = {};
  for (const s of samples) {
    const d = new Date(s.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    let label: string;
    if (d.toDateString() === today.toDateString()) label = "היום";
    else if (d.toDateString() === yesterday.toDateString()) label = "אתמול";
    else
      label = d.toLocaleDateString("he-IL", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    if (!groups[label]) groups[label] = [];
    groups[label].push(s);
  }
  return groups;
}

export function VoicePanelToggle({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="פתחי את פיד הקול"
      onClick={onClick}
      className="relative flex h-9 w-9 items-center justify-center rounded-lg border bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      <MessageCircle className="h-[18px] w-[18px]" />
    </button>
  );
}

export function VoicePanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { samples, settings, updateSettings } = useVoiceChannel();
  const grouped = groupByDay(samples);

  return (
    <aside
      aria-hidden={!open}
      className={cn(
        "shrink-0 overflow-hidden border-s bg-white transition-[width] duration-200 ease-out",
        open ? "w-[400px]" : "w-0",
      )}
    >
      <div className="w-[400px] h-full flex flex-col bg-white">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between gap-3 border-b px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-[#1a1a1a]">פיד קולי</h2>
            <p className="text-xs text-[#666]">
              🎙️ {samples.length} הודעות
            </p>
          </div>
          <div className="flex items-center gap-3">
            <AutoplayToggle
              enabled={!!settings.voice_autoplay}
              onChange={(v) => updateSettings({ voice_autoplay: v })}
            />
            <button
              type="button"
              onClick={onClose}
              aria-label="סגירת פיד קולי"
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Timeline */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {samples.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-4xl mb-3">🎙️</div>
              <p className="text-sm text-[#999]">אין עדיין הודעות קוליות</p>
              <p className="text-xs text-[#bbb] mt-1">הודעות מהסוכנים שלך יופיעו כאן</p>
            </div>
          ) : (
            Object.entries(grouped).map(([label, msgs]) => (
              <div key={label}>
                <div className="py-3">
                  <span className="text-xs font-semibold text-[#999] tracking-wider">{label}</span>
                </div>
                <div className="space-y-px">
                  {msgs.map((sample) => {
                    const agentKey = (sample.agent_name || "").toLowerCase();
                    const meta = agentKey ? AGENT_META[agentKey] : null;
                    return (
                      <div
                        key={sample.id}
                        className="py-3.5 px-3 -mx-3 rounded-xl hover:bg-black/[0.02] transition-colors group space-y-1.5"
                      >
                        <div className="flex items-center gap-2">
                          <AgentAvatar agentName={sample.agent_name} />
                          {meta && (
                            <span className="text-sm font-medium text-[#1a1a1a]">{meta.label}</span>
                          )}
                          <span className="text-[10px] text-[#999] tabular-nums">
                            {formatTime(sample.created_at)}
                          </span>
                          <div className="flex items-center gap-0.5 ms-auto transition-opacity opacity-0 group-hover:opacity-100">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(sample.text_content);
                              }}
                              className="p-1 rounded-md hover:bg-black/5 text-[#999] hover:text-[#666] transition-colors"
                              title="העתקת טקסט"
                            >
                              <Copy className="w-3.5 h-3.5" />
                            </button>
                            <a
                              href={sample.audio_url}
                              download
                              onClick={(e) => e.stopPropagation()}
                              className="p-1 rounded-md hover:bg-black/5 text-[#999] hover:text-[#666] transition-colors"
                              title="הורדת הקלטה"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                          </div>
                        </div>
                        <ExpandableText text={sample.text_content} />
                        <VoicePlayerInline audioUrl={sample.audio_url} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </aside>
  );
}
